import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, ApiError } from "@/lib/api";
import { trackConversion } from "@/lib/analytics";
import { useAuthRefresh } from "@/hooks/useAuth";
import { Button, Input, Callout, Spinner } from "@/components/ui";

type LoginStep = "email" | "verify";

interface LoginFormProps {
  /** Where to redirect after successful login. Defaults to "/dashboard" */
  redirectTo?: string;
  /** Callback fired after successful authentication */
  onSuccess?: () => void;
  /** Pre-populate the email field */
  initialEmail?: string;
  /** Show a "session expired" banner above the form */
  sessionExpired?: boolean;
}

interface SendLinkResponse {
  message: string;
  session: string;
  destination: string;
  expires_in: number;
}

interface VerifyResponse {
  user: {
    id: string;
    email: string;
    name?: string;
    status: string;
  };
  is_new_user?: boolean;
  starter_key?: string;
}

const RESEND_COOLDOWN_SECONDS = 30;

export default function LoginForm({
  redirectTo = "/dashboard",
  onSuccess,
  initialEmail = "",
  sessionExpired = false,
}: LoginFormProps) {
  const navigate = useNavigate();
  const { refreshAuth } = useAuthRefresh();

  // Step management
  const [step, setStep] = useState<LoginStep>("email");

  // Email step state
  const [email, setEmail] = useState(initialEmail);
  const [session, setSession] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Verify step state
  const [digits, setDigits] = useState<string[]>(["", "", "", "", "", ""]);
  const [verifyStatus, setVerifyStatus] = useState<"idle" | "success" | "error">("idle");
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const emailInputRef = useRef<HTMLInputElement | null>(null);

  // Focus email input on mount
  useEffect(() => {
    if (step === "email") {
      const timer = setTimeout(() => {
        emailInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [step]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Email validation
  function isValidEmail(value: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  const emailIsValid = email.trim().length > 0 && isValidEmail(email);

  // Handle email submission
  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }

    if (!isValidEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post<SendLinkResponse>("/web/auth/send-link", {
        email: email.toLowerCase().trim(),
      });

      setSession(response.session);
      setStep("verify");

      // Focus first digit input after transition
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Something went wrong. Please try again.");
      }
    }
    setIsLoading(false);
  }

  // Verify code
  const verifyCode = useCallback(
    async (code: string) => {
      if (isLoading) return;

      setIsLoading(true);
      setVerifyStatus("idle");

      try {
        const result = await api.post<VerifyResponse>("/web/auth/verify", {
          email,
          code,
          session,
        });

        setVerifyStatus("success");
        trackConversion("sign_up", { method: "email" });
        trackConversion("generate_lead", { currency: "USD", value: 5 });

        // Refresh auth state
        await refreshAuth();

        // For new users with a starter key, store it for the welcome modal
        const isNewUser = result.is_new_user && result.starter_key;
        if (isNewUser) {
          try {
            sessionStorage.setItem("fhirfly_starter_key", result.starter_key!);
          } catch {
            // sessionStorage unavailable — no modal, but user still has the credential
          }
        }

        // Brief delay to show success, then redirect/callback
        setTimeout(() => {
          if (onSuccess) {
            onSuccess();
          } else {
            // New users go to credentials page to see their starter key
            navigate(isNewUser ? "/dashboard/credentials" : redirectTo, { replace: true });
          }
        }, 300);
      } catch {
        setVerifyStatus("error");
        setIsLoading(false);

        // Clear digits after error
        setTimeout(() => {
          setDigits(["", "", "", "", "", ""]);
          setVerifyStatus("idle");
          inputRefs.current[0]?.focus();
        }, 1500);
      }
    },
    [email, session, redirectTo, navigate, refreshAuth, onSuccess, isLoading]
  );

  // Check if all digits filled and trigger verify
  useEffect(() => {
    const code = digits.join("");
    if (code.length === 6 && !isLoading && verifyStatus === "idle" && step === "verify") {
      const timer = setTimeout(() => verifyCode(code), 0);
      return () => clearTimeout(timer);
    }
  }, [digits, isLoading, verifyStatus, verifyCode, step]);

  // Handle digit input
  function handleDigitInput(index: number, value: string) {
    const digit = value.replace(/\D/g, "").slice(-1);
    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);

    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  // Handle backspace and arrows
  function handleKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newDigits = [...digits];
      if (digits[index]) {
        newDigits[index] = "";
        setDigits(newDigits);
      } else if (index > 0) {
        newDigits[index - 1] = "";
        setDigits(newDigits);
        inputRefs.current[index - 1]?.focus();
      }
    }
    if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowRight" && index < 5) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  }

  // Handle paste
  function handlePaste(e: React.ClipboardEvent) {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length > 0) {
      const newDigits = [...digits];
      for (let i = 0; i < 6; i++) {
        newDigits[i] = pasted[i] || "";
      }
      setDigits(newDigits);
      const nextEmpty = newDigits.findIndex((d) => !d);
      const focusIndex = nextEmpty === -1 ? 5 : nextEmpty;
      inputRefs.current[focusIndex]?.focus();
    }
  }

  // Resend code
  async function handleResend() {
    if (resendCooldown > 0 || isLoading) return;

    setIsLoading(true);
    try {
      const response = await api.post<SendLinkResponse>("/web/auth/send-link", { email });
      setSession(response.session);
      setDigits(["", "", "", "", "", ""]);
      setVerifyStatus("idle");
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      inputRefs.current[0]?.focus();
    } catch {
      // Silent fail
    }
    setIsLoading(false);
  }

  // Go back to email step
  function handleBackToEmail() {
    setStep("email");
    setDigits(["", "", "", "", "", ""]);
    setVerifyStatus("idle");
    setError(null);
  }

  // EMAIL STEP
  if (step === "email") {
    return (
      <main className="py-12 px-4 md:py-16">
        <div className="max-w-sm mx-auto text-center space-y-6">
          {sessionExpired && (
            <Callout variant="warning">
              Your session has expired. Please sign in again to continue.
            </Callout>
          )}

          <div className="space-y-3">
            <h1 className="heading-xl">Welcome to FHIRfly</h1>
            <p className="body-m text-text-quiet">
              Enter your email to sign in or create an account. We&apos;ll send you a verification code.
            </p>
          </div>

          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <Input
              ref={emailInputRef}
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && emailIsValid && !isLoading) {
                  e.preventDefault();
                  handleEmailSubmit(e);
                }
              }}
              placeholder="you@example.com"
              inputSize="lg"
              inputMode="email"
              disabled={isLoading}
              leftIcon={<i className="fa-solid fa-envelope" />}
            />

            {error && (
              <Callout variant="danger">
                {error}
              </Callout>
            )}

            <Button
              variant="brand-accent"
              size="lg"
              type="submit"
              disabled={!emailIsValid || isLoading}
              className="w-full"
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" className="border-white border-t-transparent" />
                  Sending code...
                </>
              ) : (
                "Continue with email"
              )}
            </Button>
          </form>

          <p className="text-xs text-text-quiet">
            By continuing, you agree to our{" "}
            <Link to="/docs/legal/terms" className="text-brand-500 underline">Terms of Service</Link>
            {" "}and{" "}
            <Link to="/docs/legal/privacy" className="text-brand-500 underline">Privacy Policy</Link>.
          </p>
        </div>
      </main>
    );
  }

  // VERIFY STEP
  const maskedEmail = email.replace(/(.{2})(.*)(@.*)/, "$1***$3");

  return (
    <main className="py-12 px-4 md:py-16">
      <div className="max-w-md mx-auto text-center space-y-6">
        <div className="space-y-3">
          <h1 className="heading-xl">Check your email</h1>
          <p className="body-m text-text-quiet">
            We sent a 6-digit code to <strong>{maskedEmail}</strong>
          </p>
        </div>

        <div className="flex flex-col items-center gap-3">
          <div className="flex items-center justify-center gap-2">
            {digits.map((digit, index) => (
              <input
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                className="input w-14 h-14 text-center font-mono font-semibold text-xl"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={digit}
                onChange={(e) => handleDigitInput(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                disabled={isLoading}
                autoComplete="one-time-code"
              />
            ))}

            <div className="w-10 flex justify-center items-center">
              {isLoading && verifyStatus === "idle" && (
                <Spinner size="lg" />
              )}
              {verifyStatus === "success" && (
                <i className="fa-solid fa-circle-check text-3xl text-success" />
              )}
              {verifyStatus === "error" && (
                <i className="fa-solid fa-circle-xmark text-3xl text-danger" />
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-center gap-4">
          <Button
            variant="neutral-plain"
            onClick={handleResend}
            disabled={resendCooldown > 0 || isLoading}
          >
            {resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : "Resend code"}
          </Button>
          <Button
            variant="neutral-plain"
            onClick={handleBackToEmail}
            disabled={isLoading}
          >
            Use a different email
          </Button>
        </div>
      </div>
    </main>
  );
}
